/**
 * Data Path Constants
 *
 * Path templates for accessing user input data from different screen types.
 * Single source of truth for data path formats.
 *
 * @module screen-utils
 */

import type { DataPathCategory } from '../types/data-path.types';

/**
 * Path templates by category.
 * These define how to access user input data in screen struct.
 */
export const DATA_PATH_TEMPLATES = {
  /** List-based screens (single/multi selection): struct.list.checked */
  list: 'list.checked',

  /** Field screens (text/number input): struct.field.value */
  field: 'field.value',

  /** Picker screens: struct.picker.currentValue */
  picker: 'picker.currentValue',

  /** CustomScreen input template (use %s for input key): struct.input['key'].value */
  customInput: "input['%s'].value",
} as const;

/**
 * Screen type to path category mapping.
 * Determines which path template to use for each screen type.
 */
export const SCREEN_TYPE_TO_CATEGORY: Record<string, DataPathCategory | null> = {
  // List screens (multi-select) - dataType: array
  'ScreenTableMultipleSelection': 'list',
  'ScreenTwoColumnMultipleSelection': 'list',
  'ScreenImageTitleSubtitleMultipleSelectionList': 'list',

  // List screens (single-select) - dataType: number
  'ScreenTableSingleSelection': 'list',
  'ScreenTwoColumnSingleSelection': 'list',

  // Field screens - dataType: string or number (depends on field.type)
  'ScreenTitleSubtitleField': 'field',

  // Picker screens - dataType: string
  'ScreenTitleSubtitlePicker': 'picker',
  'ScreenImageTitleSubtitlePicker': 'picker',

  // CustomScreen - special handling, multiple inputs
  'CustomScreen': 'customInput',
} as const;

/**
 * Screen types that produce array data (multi-select).
 */
export const ARRAY_DATA_SCREEN_TYPES: string[] = [
  'ScreenTableMultipleSelection',
  'ScreenTwoColumnMultipleSelection',
  'ScreenImageTitleSubtitleMultipleSelectionList',
];

/**
 * Screen types that produce number data (single-select index).
 */
export const NUMBER_DATA_SCREEN_TYPES: string[] = [
  'ScreenTableSingleSelection',
  'ScreenTwoColumnSingleSelection',
];

/**
 * Regex for parsing CustomScreen input paths.
 * Matches: screenId.input['inputKey'].value
 * Groups: [1] = screenId, [2] = inputKey
 */
export const CUSTOM_INPUT_PATH_REGEX = /^(.+?)\.input\['(.+?)'\]\.value$/;

/**
 * Regex for parsing standard data paths.
 * Matches: screenId.category.suffix
 */
export const STANDARD_PATH_REGEX = /^([^.]+)\.(.+)$/;
